import { useEffect, useReducer, useRef } from "react";
import {
  calculateHexColor,
  calculateHsv,
  calculateRgbFromHexColor,
  calculateRgbFromHsv,
  coerceHexColorInput,
  IColorPickerHsv,
  IColorPickerRgb,
  IColorPickerRgbInput,
  isValidHexColor,
  isValidRgbInput,
  rgbInputToRgb,
  rgbToRgbInput,
} from "./colorPickerHelpers";

interface IColorPickerState {
  hexColorInput: string;
  rgb: IColorPickerRgb;
  rgbInput: IColorPickerRgbInput;
  hsv: IColorPickerHsv;
}

interface IColorPickerUpdateHexColorInput {
  type: "UPDATE_HEX_COLOR_INPUT";
  payload: {
    hexColorInput: string;
  };
}

interface IColorPickerUpdateRgbInput {
  type: "UPDATE_RGB_INPUT";
  payload: {
    key: keyof IColorPickerRgbInput;
    value: string;
  };
}

interface IColorPickerUpdateHsv {
  type: "UPDATE_HSV";
  payload: {
    hsv: IColorPickerHsv;
  };
}

type TColorPickerAction = IColorPickerUpdateHexColorInput | IColorPickerUpdateRgbInput | IColorPickerUpdateHsv;

function colorPickerInitializer(hexColor: string): IColorPickerState {
  const hexColorInput = hexColor ?? "";
  const rgb = calculateRgbFromHexColor(hexColor);
  const rgbInput = rgbToRgbInput(rgb);
  const hsv = calculateHsv(rgb);

  return {
    hexColorInput,
    rgb,
    rgbInput,
    hsv,
  };
}

function colorPickerReducer(state: IColorPickerState, action: TColorPickerAction): IColorPickerState {
  switch (action.type) {
    case "UPDATE_HEX_COLOR_INPUT": {
      const updatedHexColorInput = action.payload.hexColorInput;

      if (updatedHexColorInput === "") {
        return colorPickerInitializer(updatedHexColorInput);
      }

      let derivedRgb = state.rgb;
      let derivedRgbInput = state.rgbInput;
      let derivedHsv = state.hsv;

      if (isValidHexColor(updatedHexColorInput)) {
        derivedRgb = calculateRgbFromHexColor(updatedHexColorInput);
        derivedRgbInput = rgbToRgbInput(derivedRgb);
        derivedHsv = calculateHsv(derivedRgb);
      }

      return {
        ...state,
        hexColorInput: updatedHexColorInput,
        rgb: derivedRgb,
        rgbInput: derivedRgbInput,
        hsv: derivedHsv,
      };
    }
    case "UPDATE_RGB_INPUT": {
      const updatedRgbInput: IColorPickerRgbInput = {
        ...state.rgbInput,
        [action.payload.key]: action.payload.value,
      };

      let derivedRgb = state.rgb;
      let derivedHexColorInput = state.hexColorInput;
      let derivedHsv = state.hsv;

      if (isValidRgbInput(updatedRgbInput)) {
        derivedRgb = rgbInputToRgb(updatedRgbInput);
        derivedHexColorInput = calculateHexColor(derivedRgb);
        derivedHsv = calculateHsv(derivedRgb);
      }

      return {
        ...state,
        hexColorInput: derivedHexColorInput,
        rgb: derivedRgb,
        rgbInput: updatedRgbInput,
        hsv: derivedHsv,
      };
    }
    case "UPDATE_HSV": {
      const updatedHsv = action.payload.hsv;
      const rgb = calculateRgbFromHsv(updatedHsv);
      const rgbInput = rgbToRgbInput(rgb);
      const hexColorInput = calculateHexColor(rgb);

      return {
        ...state,
        hexColorInput,
        rgb,
        rgbInput,
        hsv: updatedHsv,
      };
    }
    default:
      return state;
  }
}

interface IColorPickerController {
  hexColorInput: string;
  rgbInput: IColorPickerRgbInput;
  rgb: IColorPickerRgb;
  hsv: IColorPickerHsv;
  dispatchUpdateHexColorInput: (updatedHexColorInput: string) => void;
  dispatchUpdateRgbInput: (key: keyof IColorPickerRgbInput, value: string) => void;
  dispatchUpdateHsv: (updatedHsv: IColorPickerHsv) => void;
}

export function useColorPickerController(
  hexColor: string,
  updateHexColor: (updatedHexColor: string | null) => void,
): IColorPickerController {
  const [state, dispatch] = useReducer(colorPickerReducer, hexColor, colorPickerInitializer);
  const hexColorRef = useRef(hexColor);
  const previousExternalHexColorRef = useRef(hexColor);
  const updateHexColorRef = useRef(updateHexColor);

  hexColorRef.current = hexColor;
  updateHexColorRef.current = updateHexColor;

  function commitHexColor(nextHexColor: string | null): void {
    const currentHexColor = hexColorRef.current;
    if (nextHexColor === null) {
      if ((currentHexColor ?? "") === "") {
        return;
      }
      updateHexColorRef.current(null);
      return;
    }

    if ((nextHexColor ?? "") === (currentHexColor ?? "")) {
      return;
    }

    updateHexColorRef.current(nextHexColor);
  }

  useEffect(() => {
    if (previousExternalHexColorRef.current === hexColor) {
      return;
    }

    previousExternalHexColorRef.current = hexColor;
    dispatch({
      type: "UPDATE_HEX_COLOR_INPUT",
      payload: { hexColorInput: coerceHexColorInput(hexColor ?? "") },
    });
  }, [hexColor]);

  function dispatchUpdateHexColorInput(updatedHexColorInput: string) {
    const coercedHexColorInput = coerceHexColorInput(updatedHexColorInput);
    dispatch({
      type: "UPDATE_HEX_COLOR_INPUT",
      payload: { hexColorInput: coercedHexColorInput },
    });

    if (coercedHexColorInput === "") {
      commitHexColor(null);
      return;
    }

    if (isValidHexColor(coercedHexColorInput)) {
      commitHexColor(coercedHexColorInput);
    }
  }

  function dispatchUpdateRgbInput(key: keyof IColorPickerRgbInput, value: string) {
    const updatedRgbInput: IColorPickerRgbInput = {
      ...state.rgbInput,
      [key]: value,
    };

    dispatch({ type: "UPDATE_RGB_INPUT", payload: { key, value } });

    if (!isValidRgbInput(updatedRgbInput)) {
      return;
    }

    commitHexColor(calculateHexColor(rgbInputToRgb(updatedRgbInput)));
  }

  function dispatchUpdateHsv(updatedHsv: IColorPickerHsv) {
    dispatch({ type: "UPDATE_HSV", payload: { hsv: updatedHsv } });
    commitHexColor(calculateHexColor(calculateRgbFromHsv(updatedHsv)));
  }

  return {
    hexColorInput: state.hexColorInput,
    rgbInput: state.rgbInput,
    rgb: state.rgb,
    hsv: state.hsv,
    dispatchUpdateHexColorInput,
    dispatchUpdateRgbInput,
    dispatchUpdateHsv,
  };
}
